<template>
	<button
		v-if="isAdminEfChampion && school"
		class="favourite"
		:class="{
			favourited: isResourceFavourited(resource.ID),
		}"
		:title="`Bookmark this resource for ${school.post_title}.`"
		@click="toggleResourceFavourite(resource)"
	>
		<BookmarkIconComponent />
	</button>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue';
import { useResourceStore } from '@/stores/resourceStore';
import { mapActions, mapState } from 'pinia';
import type { Resource } from '@/interfaces/resourceInterface';
import BookmarkIconComponent from '@/images/bookmark.svg?component';
import { useUser } from '@/stores/userStore';

export default defineComponent({
	components: { BookmarkIconComponent },
	props: {
		resource: {
			type: Object as PropType<Resource>,
			required: true,
		},
	},
	computed: {
		...mapState(useUser, ['school', 'isAdminEfChampion']),
		...mapState(useResourceStore, ['isResourceFavourited']),
	},
	methods: {
		...mapActions(useResourceStore, ['toggleResourceFavourite']),
	},
});
</script>

<style scoped lang="scss">
.favourite {
	// The icon has two shapes: `.fill` is the solid bookmark silhouette and
	// `.outline` is the frame/border. We target them by class (not element)
	// so styling survives SVGO shape/path conversion. Colours live here rather
	// than inline on the SVG so the favourited/hover states below can override.
	// Default (un-favourited): transparent body + grey outline. `.outline` uses
	// evenodd so it renders as a ring instead of a solid grey shape.
	:deep() {
		svg {
			.fill {
				fill: none;
				transition: fill 0.2s ease-in-out;
			}

			.outline {
				fill: #999999;
				fill-rule: evenodd;
				transition: fill 0.2s ease-in-out;
			}
		}
	}

	// Favourited/hover: fill the whole bookmark honey, not just the outline.
	&.favourited,
	&:hover {
		:deep() {
			svg {
				.fill,
				.outline {
					fill: rgb(var(--rs-color-brand-honey));
				}
			}
		}
	}
}
</style>
